import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import RadarSweep from "@/components/RadarSweep";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-24 md:pt-40 md:pb-32"
    >
      {/* backdrop grid */}
      <div className="pointer-events-none absolute inset-0 hud-grid radial-fade opacity-40" />
      {/* horizon glow */}
      <div className="pointer-events-none absolute inset-x-0 top-1/3 h-[380px] bg-[radial-gradient(ellipse_at_center,rgba(46,119,136,0.16),transparent_70%)]" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid gap-16 md:grid-cols-12 md:gap-10">
          {/* Left copy */}
          <div className="md:col-span-7">
            <motion.div
              {...fadeUp}
              className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.28em] text-mist"
            >
              <span className="h-1.5 w-1.5 bg-signalGreen animate-pulse-soft" />
              System status · Operational
              <span className="hidden text-mist/50 md:inline">
                &nbsp;·&nbsp; Bearing 034° &nbsp;·&nbsp; Altitude nominal
              </span>
            </motion.div>

            <motion.h1
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.08 }}
              className="mt-8 text-balance font-display text-5xl leading-[1.02] tracking-tight text-ink md:text-6xl lg:text-7xl"
            >
              Situational awareness
              <br />
              <span className="italic text-mist">for the life</span> you
              actually live.
            </motion.h1>

            <motion.p
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.16 }}
              className="mt-8 max-w-xl text-lg leading-relaxed text-mist"
            >
              Life OS is a life capacity and resilience platform for modern
              households. Not another productivity app — a quiet tower that
              watches the horizon, measures what you can carry, and protects
              the room you need to recover.
            </motion.p>

            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.24 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <a
                href="#waitlist"
                data-testid="hero-cta-waitlist"
                className="group inline-flex items-center gap-2 bg-teal px-6 py-3.5 font-mono text-[12px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-teal-hover"
              >
                Request access
                <ArrowRight
                  className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5"
                  strokeWidth={1.5}
                />
              </a>
              <Link
                to="/login"
                data-testid="hero-cta-preview"
                className="inline-flex items-center gap-2 border border-rule px-6 py-3.5 font-mono text-[12px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:border-amber"
              >
                Sign in
              </Link>
            </motion.div>

            {/* small readouts */}
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.35 }}
              className="mt-14 grid max-w-lg grid-cols-3 gap-8 border-t border-rule pt-6"
            >
              {[
                ["Capacity", "62%", "signalAmber"],
                ["Compression", "0.71", "signalAmber"],
                ["Recovery", "18h", "signalGreen"],
              ].map(([k, v, tone]) => (
                <div key={k}>
                  <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                    {k}
                  </div>
                  <div
                    className={`mt-2 font-mono text-2xl ${
                      tone === "signalGreen"
                        ? "text-signalGreen"
                        : tone === "signalRed"
                          ? "text-signalRed"
                          : "text-signalAmber"
                    }`}
                  >
                    {v}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right radar */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative md:col-span-5"
          >
            <div className="relative mx-auto max-w-md">
              {/* corner brackets */}
              <span className="absolute -top-2 -left-2 h-4 w-4 border-t border-l border-teal/70" />
              <span className="absolute -top-2 -right-2 h-4 w-4 border-t border-r border-teal/70" />
              <span className="absolute -bottom-2 -left-2 h-4 w-4 border-b border-l border-teal/70" />
              <span className="absolute -bottom-2 -right-2 h-4 w-4 border-b border-r border-teal/70" />

              <div className="relative border border-rule bg-deep/60 p-8 backdrop-blur-sm">
                <div className="mb-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                  <span>Tower · Local scan</span>
                  <span className="text-teal">LIVE</span>
                </div>
                <RadarSweep />
                <div className="mt-6 space-y-2 font-mono text-[11px] text-mist">
                  <div className="flex items-center justify-between">
                    <span>· birthday inbound (72h)</span>
                    <span className="text-signalAmber">watch</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>· quiet evening secured</span>
                    <span className="text-signalGreen">hold</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>· caregiver visit (Thu)</span>
                    <span className="text-mist">plan</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
